import type { FieldOf } from "@fobu/schema";
import { seededShuffle } from "@fobu/engine";
import { useMemo } from "preact/hooks";
import { useFormEnv } from "../../lib/context";
import type { FieldProps } from "./types";

/**
 * Matrix als Liste von Zeilen-Gruppen (je fieldset/legend). Auf breiten Bildschirmen per CSS-Grid
 * als Tabelle mit gemeinsamer Kopfzeile; auf dem Handy untereinander – ohne horizontales Scrollen.
 */
export function Matrix({ field, value, onChange, a11y }: FieldProps<FieldOf<"matrix">>) {
  const { ctl } = useFormEnv();
  const rows = useMemo(() => (field.randomizeRows ? seededShuffle(field.rows, ctl.seed + field.id) : field.rows), [field, ctl.seed]);
  const current = (value && typeof value === "object" && !Array.isArray(value) ? value : {}) as Record<string, string | string[]>;

  const set = (rowId: string, colId: string, checked: boolean) => {
    const next = { ...current };
    if (field.multiple) {
      const list = Array.isArray(next[rowId]) ? [...(next[rowId] as string[])] : [];
      const updated = checked ? [...list.filter((c) => c !== colId), colId] : list.filter((c) => c !== colId);
      if (updated.length) next[rowId] = updated;
      else delete next[rowId];
    } else next[rowId] = colId;
    onChange(Object.keys(next).length ? next : undefined);
  };

  return (
    <div class="fb-matrix" style={{ "--fb-cols": field.columns.length }}>
      <div class="fb-matrix-head" aria-hidden="true">
        <span />
        {field.columns.map((c) => (
          <span key={c.id}>{c.label}</span>
        ))}
      </div>
      {rows.map((row) => {
        const cell = current[row.id];
        const missing = a11y.invalid && field.required && cell === undefined;
        return (
          // Kein <fieldset>/<legend>: Safari ordnet <legend> nicht ins Grid ein – die Spalten verrutschen.
          // role="radiogroup"/"group" mit aria-labelledby ist für Screenreader gleichwertig.
          <div
            key={row.id}
            role={field.multiple ? "group" : "radiogroup"}
            aria-labelledby={`${a11y.id}-${row.id}-label`}
            aria-invalid={missing || undefined}
            class={`fb-matrix-row${missing ? " is-invalid" : ""}`}
          >
            <div class="fb-matrix-rowlabel" id={`${a11y.id}-${row.id}-label`}>
              {row.label}
            </div>
            <div class="fb-matrix-cells">
              {field.columns.map((col) => {
                const checked = Array.isArray(cell) ? cell.includes(col.id) : cell === col.id;
                return (
                  <label key={col.id} class={`fb-matrix-cell${checked ? " is-checked" : ""}`}>
                    <input
                      class="fb-option-input"
                      type={field.multiple ? "checkbox" : "radio"}
                      name={`${a11y.id}-${row.id}`}
                      checked={checked}
                      onChange={(e) => set(row.id, col.id, e.currentTarget.checked)}
                    />
                    <span class={`fb-mark fb-mark--${field.multiple ? "checkbox" : "radio"}`} aria-hidden="true" />
                    <span class="fb-matrix-collabel">{col.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
